// components/Sections/Admin/Reporteria/VentasUnificadas.jsx
import React, { useState, useCallback, useEffect } from 'react';
import {
  Table,
  DatePicker,
  Select,
  Button,
  Card,
  Tag,
  Space,
  Row,
  Col,
  Tooltip,
  message,
  Input,
  Alert,
  Typography,
  Badge,
  Modal,
} from 'antd';
import {
  FilterOutlined,
  DownloadOutlined,
  ReloadOutlined,
  UserOutlined,
  IdcardOutlined,
  MailOutlined,
  ShopOutlined,
  LaptopOutlined,
  EyeOutlined,
  StopOutlined,
  FileTextOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import Reporteria from '../../../../service/Reporteria';
import RecintoService from '../../../../service/Recintos';
import VentaPresencialSvc from '../../../../service/VentaPresencial';
import DetalleVentaDrawer from '../Ventas/DetalleVentaDrawer';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

const ESTADOS_PRESENCIAL = ['pagada', 'pendiente', 'anulada', 'reservada'];
const ESTADOS_ONLINE = ['completada', 'pendiente', 'cancelada', 'expirada'];

const VentasUnificadas = () => {
  const [ventas, setVentas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [ventaDetalle, setVentaDetalle] = useState(null);
  const [anularModal, setAnularModal] = useState({ visible: false, venta: null });
  const [motivoAnulacion, setMotivoAnulacion] = useState('');
  const [loadingAnular, setLoadingAnular] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [recintos, setRecintos] = useState([]);

  const [filtros, setFiltros] = useState({
    tipoVenta: undefined,
    estado: undefined,
    recinto: undefined,
    fechaDesde: null,
    fechaHasta: null,
    nombre: '',
    rut: '',
    email: '',
    page: 1,
    limit: 20,
  });

  const cargarVentas = useCallback(async (params = filtros) => {
    setLoading(true);
    try {
      const query = {};
      if (params.tipoVenta) query.tipoVenta = params.tipoVenta;
      if (params.estado) query.estado = params.estado;
      if (params.fechaDesde) query.fechaDesde = params.fechaDesde;
      if (params.fechaHasta) query.fechaHasta = params.fechaHasta;
      if (params.nombre) query.nombre = params.nombre;
      if (params.rut) query.rut = params.rut;
      if (params.email) query.email = params.email;
      if (params.recinto) query.recinto = params.recinto;
      query.page = params.page || 1;
      query.limit = params.limit || 20;

      const res = await Reporteria.GetVentasUnificadas(query);
      const { ventas: data, total, page, porPagina } = res;

      console.log('Ventas unificadas cargadas:', data);
      setVentas(data || []);
      setPagination({ current: page, pageSize: porPagina, total });
    } catch (error) {
      console.error('Error cargando ventas unificadas:', error);
      message.error('Error al cargar las ventas');
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => {
    cargarVentas();
    RecintoService.List({ limit: 1000 }).then(res => setRecintos(res.data || [])).catch(() => {});
  }, []);

  const aplicarFiltros = () => {
    const nuevos = { ...filtros, page: 1 };
    setFiltros(nuevos);
    cargarVentas(nuevos);
  };

  const limpiarFiltros = () => {
    const limpios = {
      tipoVenta: undefined,
      estado: undefined,
      recinto: undefined,
      fechaDesde: null,
      fechaHasta: null,
      nombre: '',
      rut: '',
      email: '',
      page: 1,
      limit: 20,
    };
    setFiltros(limpios);
    cargarVentas(limpios);
  };

  const handleTableChange = (newPagination) => {
    const nuevos = { ...filtros, page: newPagination.current, limit: newPagination.pageSize };
    setFiltros(nuevos);
    cargarVentas(nuevos);
  };

  const handleFechaChange = (dates) => {
    setFiltros(prev => ({
      ...prev,
      fechaDesde: dates ? dates[0].format('YYYY-MM-DD') : null,
      fechaHasta: dates ? dates[1].format('YYYY-MM-DD') : null,
    }));
  };

  const exportarExcel = async () => {
    try {
      setLoadingExport(true);
      message.loading({ content: 'Generando archivo Excel...', key: 'exportar', duration: 0 });

      // El .xlsx lo arma el backend en segundo plano y se baja desde S3 (ver
      // ExportarVentasUnificadasExcel). Ni pedir el listado en JSON (limit=9999)
      // ni bajar el archivo en la misma request aguantan rangos largos: superan
      // los 29 s de API Gateway y los 6 MB de respuesta de Lambda.
      const query = {};
      if (filtros.tipoVenta) query.tipoVenta = filtros.tipoVenta;
      if (filtros.estado) query.estado = filtros.estado;
      if (filtros.fechaDesde) query.fechaDesde = filtros.fechaDesde;
      if (filtros.fechaHasta) query.fechaHasta = filtros.fechaHasta;
      if (filtros.nombre) query.nombre = filtros.nombre;
      if (filtros.rut) query.rut = filtros.rut;
      if (filtros.email) query.email = filtros.email;
      if (filtros.recinto) query.recinto = filtros.recinto;

      const resultado = await Reporteria.ExportarVentasUnificadasExcel(query, {
        onEstado: (estado) => {
          if (estado === 'procesando') {
            message.loading({
              content: 'Generando archivo Excel... con rangos largos puede tardar un par de minutos',
              key: 'exportar',
              duration: 0
            });
          }
        }
      });

      const filas = resultado?.filas != null ? ` (${resultado.filas.toLocaleString('es-CL')} ventas)` : '';
      message.success({ content: `Excel generado correctamente${filas}`, key: 'exportar' });
    } catch (error) {
      console.error('Error exportando:', error);
      message.error({ content: error?.error || error?.message || 'Error al exportar el Excel', key: 'exportar', duration: 6 });
    } finally {
      setLoadingExport(false);
    }
  };

  const abrirAnularModal = (venta) => {
    setMotivoAnulacion('');
    setAnularModal({ visible: true, venta });
  };

  const cerrarAnularModal = () => {
    setAnularModal({ visible: false, venta: null });
    setMotivoAnulacion('');
  };

  const confirmarAnulacion = async () => {
    if (!motivoAnulacion.trim() || motivoAnulacion.trim().length < 5) {
      message.warning('Ingresa un motivo de al menos 5 caracteres');
      return;
    }
    setLoadingAnular(true);
    try {
      const { venta } = anularModal;
      await VentaPresencialSvc.AnularVenta(venta.id, venta.tipoVenta, motivoAnulacion.trim());
      message.success('Venta anulada exitosamente');
      cerrarAnularModal();
      cargarVentas();
    } catch (e) {
      message.error(e?.message || 'Error al anular la venta');
    } finally {
      setLoadingAnular(false);
    }
  };

  const columns = [
    {
      title: 'Tipo',
      dataIndex: 'tipoVenta',
      key: 'tipoVenta',
      width: 110,
      fixed: 'left',
      render: (tipo) =>
        tipo === 'presencial' ? (
          <Tag color="blue" icon={<ShopOutlined />}>Presencial</Tag>
        ) : (
          <Tag color="blue" icon={<LaptopOutlined />}>Online</Tag>
        ),
    },
    {
      title: 'Código',
      dataIndex: 'codigo',
      key: 'codigo',
      width: 160,
      render: (text) => <Text code>{text}</Text>,
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      width: 120,
      render: (fecha) => (
        <Tooltip title={dayjs(fecha).format('DD/MM/YYYY HH:mm')}>
          {dayjs(fecha).format('DD/MM/YYYY')}
        </Tooltip>
      ),
      sorter: (a, b) => new Date(a.fecha) - new Date(b.fecha),
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      width: 120,
      render: (estado) => {
        const colores = {
          pagada: 'success',
          completada: 'success',
          pendiente: 'warning',
          reservada: 'processing',
          anulada: 'error',
          cancelada: 'error',
          expirada: 'default',
        };
        return <Badge status={colores[estado] || 'default'} text={estado} />;
      },
    },
    {
      title: 'Cliente',
      key: 'cliente',
      width: 200,
      ellipsis: true,
      render: (_, record) => (
        <div>
          <div>{record.cliente?.nombre}</div>
          <Text type="secondary" style={{ fontSize: 11 }}>{record.cliente?.rut}</Text>
        </div>
      ),
    },
    {
      title: 'Email',
      key: 'email',
      width: 200,
      ellipsis: true,
      render: (_, record) => record.cliente?.email,
    },
    {
      title: 'Talleres',
      dataIndex: 'talleres',
      key: 'talleres',
      width: 220,
      render: (talleres) =>
        talleres?.length > 0 ? (
          <div>
            {talleres.map((t, i) => (
              <div key={i} style={{ fontSize: 12 }}>{t.nombre}</div>
            ))}
          </div>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Recinto',
      dataIndex: 'talleres',
      key: 'recinto',
      width: 160,
      render: (talleres) => {
        const recintos = [...new Set((talleres || []).map(t => t.recinto).filter(Boolean))];
        return recintos.length > 0
          ? recintos.map((r, i) => <div key={i} style={{ fontSize: 12 }}>{r}</div>)
          : <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'Monto',
      dataIndex: 'monto',
      key: 'monto',
      width: 120,
      align: 'right',
      render: (monto) => (
        <strong style={{ color: '#0047bb' }}>
          ${monto?.toLocaleString('es-CL')}
        </strong>
      ),
      sorter: (a, b) => a.monto - b.monto,
    },
    {
      title: 'Método Pago',
      dataIndex: 'metodoPago',
      key: 'metodoPago',
      width: 120,
      render: (metodo) => (
        <Tag>{metodo?.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Boleta',
      key: 'boleta',
      width: 140,
      render: (_, record) => {
        const b = record.boleta;
        if (!b || b.estado === 'no_aplica') return <Text type="secondary">—</Text>;

        const config = {
          emitido:   { color: 'blue', label: 'Emitida' },
          parcial:   { color: 'warning', label: `Parcial ${b.totalGenerados}/${b.totalGenerados + b.totalErrores}` },
          error:     { color: 'error',   label: 'Error' },
          pendiente: { color: 'default', label: 'Pendiente' },
        }[b.estado] || { color: 'default', label: b.estado };

        const tooltip =
          b.estado === 'emitido'   ? `${b.totalGenerados} boleta(s) emitida(s)` :
          b.estado === 'parcial'   ? `${b.totalGenerados} emitida(s), ${b.totalErrores} con error` :
          b.estado === 'error'     ? `${b.totalErrores} error(es) al emitir` :
          'Aún no se ha emitido boleta';

        return (
          <Space size={4}>
            <Tooltip title={tooltip}>
              <Tag color={config.color} icon={<FileTextOutlined />}>{config.label}</Tag>
            </Tooltip>
            {b.primerUrl && (
              <Tooltip title="Ver boleta">
                <Button
                  size="small"
                  type="link"
                  icon={<LinkOutlined />}
                  onClick={(e) => { e.stopPropagation(); window.open(b.primerUrl, '_blank'); }}
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Vendedor',
      dataIndex: 'vendedor',
      key: 'vendedor',
      width: 140,
      render: (vendedor) => vendedor || <Text type="secondary">—</Text>,
    },
    {
      title: '',
      key: 'acciones',
      width: 90,
      fixed: 'right',
      render: (_, record) => {
        const puedeAnular = ['completada', 'pagada'].includes(record.estado);
        return (
          <Space size={4}>
            <Tooltip title="Ver detalle">
              <Button
                icon={<EyeOutlined />}
                size="small"
                onClick={() => setVentaDetalle(record)}
              />
            </Tooltip>
            {puedeAnular && (
              <Tooltip title="Anular venta">
                <Button
                  icon={<StopOutlined />}
                  size="small"
                  danger
                  onClick={() => abrirAnularModal(record)}
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  const hayFiltrosActivos =
    filtros.tipoVenta || filtros.estado || filtros.recinto || filtros.fechaDesde ||
    filtros.nombre || filtros.rut || filtros.email;

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title="Ventas Unificadas (Presencial + Online)"
        extra={
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={exportarExcel}
              loading={loadingExport}
              type="primary"
              style={{ backgroundColor: '#0047bb', borderColor: '#0047bb' }}
            >
              Exportar Excel
            </Button>
            <Button icon={<ReloadOutlined />} onClick={aplicarFiltros}>
              Actualizar
            </Button>
          </Space>
        }
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {/* Fila 1: búsqueda por texto */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <Input
                placeholder="Buscar por nombre..."
                prefix={<UserOutlined />}
                value={filtros.nombre}
                onChange={(e) => setFiltros(prev => ({ ...prev, nombre: e.target.value }))}
                onPressEnter={aplicarFiltros}
                allowClear
              />
            </Col>
            <Col xs={24} sm={8}>
              <Input
                placeholder="Buscar por RUT..."
                prefix={<IdcardOutlined />}
                value={filtros.rut}
                onChange={(e) => setFiltros(prev => ({ ...prev, rut: e.target.value }))}
                onPressEnter={aplicarFiltros}
                allowClear
              />
            </Col>
            <Col xs={24} sm={8}>
              <Input
                placeholder="Buscar por email..."
                prefix={<MailOutlined />}
                value={filtros.email}
                onChange={(e) => setFiltros(prev => ({ ...prev, email: e.target.value }))}
                onPressEnter={aplicarFiltros}
                allowClear
              />
            </Col>
          </Row>

          {/* Fila 2: filtros de tipo, estado, recinto y fechas */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={5}>
              <Select
                placeholder="Tipo de venta"
                style={{ width: '100%' }}
                allowClear
                value={filtros.tipoVenta}
                onChange={(v) => setFiltros(prev => ({ ...prev, tipoVenta: v }))}
              >
                <Option value="presencial">Presencial</Option>
                <Option value="online">Online</Option>
              </Select>
            </Col>
            <Col xs={24} sm={5}>
              <Select
                placeholder="Estado"
                style={{ width: '100%' }}
                allowClear
                value={filtros.estado}
                onChange={(v) => setFiltros(prev => ({ ...prev, estado: v }))}
              >
                {[...new Set([...ESTADOS_PRESENCIAL, ...ESTADOS_ONLINE])].map(e => (
                  <Option key={e} value={e}>{e}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={6}>
              <Select
                placeholder="Recinto"
                style={{ width: '100%' }}
                allowClear
                showSearch
                optionFilterProp="children"
                value={filtros.recinto}
                onChange={(v) => setFiltros(prev => ({ ...prev, recinto: v }))}
              >
                {recintos.map(r => (
                  <Option key={r._id} value={r._id}>{r.nombre}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={8}>
              <RangePicker
                placeholder={['Fecha desde', 'Fecha hasta']}
                format="DD/MM/YYYY"
                style={{ width: '100%' }}
                value={
                  filtros.fechaDesde && filtros.fechaHasta
                    ? [dayjs(filtros.fechaDesde), dayjs(filtros.fechaHasta)]
                    : null
                }
                onChange={handleFechaChange}
              />
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <Space>
                <Button type="primary" onClick={aplicarFiltros} icon={<FilterOutlined />}>
                  Filtrar
                </Button>
                <Button onClick={limpiarFiltros}>Limpiar</Button>
              </Space>
            </Col>
          </Row>

          {/* Indicador de filtros activos */}
          {hayFiltrosActivos && (
            <Alert
              message="Filtros activos"
              description={
                <Space wrap>
                  {filtros.tipoVenta && <Tag color="blue">Tipo: {filtros.tipoVenta}</Tag>}
                  {filtros.estado && <Tag color="orange">Estado: {filtros.estado}</Tag>}
                  {filtros.recinto && <Tag color="magenta">Recinto: {recintos.find(r => r._id === filtros.recinto)?.nombre}</Tag>}
                  {filtros.nombre && <Tag color="blue">Nombre: {filtros.nombre}</Tag>}
                  {filtros.rut && <Tag color="cyan">RUT: {filtros.rut}</Tag>}
                  {filtros.email && <Tag color="purple">Email: {filtros.email}</Tag>}
                  {filtros.fechaDesde && filtros.fechaHasta && (
                    <Tag color="gold">
                      {dayjs(filtros.fechaDesde).format('DD/MM/YY')} - {dayjs(filtros.fechaHasta).format('DD/MM/YY')}
                    </Tag>
                  )}
                </Space>
              }
              type="info"
              showIcon
              closable
              onClose={limpiarFiltros}
            />
          )}

          <Table
            columns={columns}
            dataSource={ventas}
            loading={loading}
            rowKey="id"
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} ventas`,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
            scroll={{ x: 1830 }}
            size="small"
            locale={{ emptyText: 'Sin ventas. Aplica filtros y presiona "Filtrar".' }}
            onRow={(record) => ({
              style: { cursor: 'pointer' },
              onDoubleClick: () => setVentaDetalle(record),
            })}
          />
        </Space>
      </Card>

      <DetalleVentaDrawer
        venta={ventaDetalle}
        onClose={() => setVentaDetalle(null)}
        onCancelada={() => cargarVentas()}
      />

      {/* ── Modal anulación ──────────────────────────────────────────────── */}
      <Modal
        title={
          <span>
            <StopOutlined style={{ color: '#ef4444', marginRight: 8 }} />
            Anular Venta
          </span>
        }
        open={anularModal.visible}
        onCancel={cerrarAnularModal}
        onOk={confirmarAnulacion}
        okText="Confirmar anulación"
        okButtonProps={{ danger: true, loading: loadingAnular }}
        cancelText="Cancelar"
        width={500}
        destroyOnClose
      >
        {anularModal.venta && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Info de la venta */}
            <div style={{ background: '#fef2f2', borderRadius: 8, padding: '12px 16px', border: '1px solid #fecaca' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <Text strong style={{ fontSize: 14 }}>{anularModal.venta.cliente?.nombre}</Text>
                <Tag color={anularModal.venta.tipoVenta === 'presencial' ? 'green' : 'blue'}>
                  {anularModal.venta.tipoVenta === 'presencial' ? 'Presencial' : 'Online'}
                </Tag>
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {anularModal.venta.codigo} · {dayjs(anularModal.venta.fecha).format('DD/MM/YYYY HH:mm')}
              </Text>
              <br />
              <Text style={{ fontSize: 13, fontWeight: 600, color: '#0047bb' }}>
                ${anularModal.venta.monto?.toLocaleString('es-CL')}
              </Text>
              {anularModal.venta.talleres?.length > 0 && (
                <div style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>
                  {anularModal.venta.talleres.map(t => t.nombre).join(' / ')}
                </div>
              )}
            </div>

            {/* Alert de advertencia */}
            <Alert
              message="Esta acción no se puede deshacer"
              description="Se cancelarán todas las inscripciones activas asociadas y se liberarán los cupos de los horarios."
              type="error"
              showIcon
            />

            {/* Motivo */}
            <div>
              <Text strong style={{ display: 'block', marginBottom: 6 }}>
                Motivo de anulación <Text type="danger">*</Text>
              </Text>
              <Input.TextArea
                value={motivoAnulacion}
                onChange={(e) => setMotivoAnulacion(e.target.value)}
                placeholder="Ej: Solicitud del cliente, error en la inscripción, duplicado..."
                rows={3}
                maxLength={300}
                showCount
                status={motivoAnulacion.length > 0 && motivoAnulacion.length < 5 ? 'error' : ''}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default VentasUnificadas;
